import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { getInitialMonth } from '../utils/format';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
function monthLabel(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(Number);
  return `${MONTH_NAMES[m-1]} ${y}`;
}

const STATUS_META = {
  planning:   { label:'Planning',   color:'#7C3AED', bg:'#F5F3FF' },
  active:     { label:'Active',     color:'#059669', bg:'#ECFDF5' },
  on_hold:    { label:'On Hold',    color:'#D97706', bg:'#FFFBEB' },
  completed:  { label:'Completed',  color:'#0369A1', bg:'#EFF6FF' },
  cancelled:  { label:'Cancelled',  color:'#DC2626', bg:'#FEF2F2' },
};
const PRIORITY_META = {
  low:    { label:'Low',    color:'#64748B' },
  medium: { label:'Medium', color:'#D97706' },
  high:   { label:'High',   color:'#EA580C' },
  urgent: { label:'Urgent', color:'#DC2626' },
};
const PROJECT_COLORS = ['#073E81','#7C3AED','#059669','#DC2626','#D97706','#0891B2','#DB2777','#65A30D'];

function progress(p) {
  const t = parseInt(p.total_tasks)||0;
  const d = parseInt(p.done_tasks)||0;
  return t > 0 ? Math.round((d/t)*100) : 0;
}

function daysDue(dateStr) {
  if (!dateStr) return null;
  const diff = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
  return diff;
}

// ── Project Form Modal ────────────────────────────────────────────────────────
function ProjectModal({ project, clients, users, onClose, onSaved }) {
  const isEdit = !!project;
  const [form, setForm] = useState({
    name:        project?.name || '',
    description: project?.description || '',
    client_id:   project?.client_id || '',
    status:      project?.status || 'active',
    priority:    project?.priority || 'medium',
    color:       project?.color || '#073E81',
    start_date:  project?.start_date?.slice(0,10) || '',
    due_date:    project?.due_date?.slice(0,10) || '',
    member_ids:  project?.members?.map(m=>m.user_id) || [],
  });
  const [saving, setSaving] = useState(false);
  const upd = (k,v) => setForm(f=>({...f,[k]:v}));

  function toggleMember(uid) {
    setForm(f => ({
      ...f,
      member_ids: f.member_ids.includes(uid)
        ? f.member_ids.filter(id => id !== uid)
        : [...f.member_ids, uid],
    }));
  }

  async function handleSave() {
    if (!form.name.trim()) return toast.error('Project name required');
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/projects/${project.id}`, form);
      } else {
        await api.post('/projects', form);
      }
      toast.success(isEdit ? 'Project updated' : 'Project created');
      onSaved();
      onClose();
    } catch(err) { toast.error(err.response?.data?.error||'Failed'); }
    finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:560, maxHeight:'90vh', overflowY:'auto' }}>
        <div className="modal-title">{isEdit ? 'Edit Project' : 'New Project'}</div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
          <div className="form-row" style={{ gridColumn:'1/-1' }}>
            <label className="form-label">Project Name *</label>
            <input className="form-input" value={form.name} onChange={e=>upd('name',e.target.value)} placeholder="e.g. Brand Identity — ACME Corp" />
          </div>
          <div className="form-row" style={{ gridColumn:'1/-1' }}>
            <label className="form-label">Description</label>
            <textarea className="form-input" rows={2} value={form.description} onChange={e=>upd('description',e.target.value)} placeholder="What is this project about?" style={{ resize:'vertical' }} />
          </div>
          <div className="form-row">
            <label className="form-label">Client</label>
            <select className="form-input" value={form.client_id} onChange={e=>upd('client_id',e.target.value)}>
              <option value="">— No client —</option>
              {clients?.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="form-row">
            <label className="form-label">Status</label>
            <select className="form-input" value={form.status} onChange={e=>upd('status',e.target.value)}>
              {Object.entries(STATUS_META).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div className="form-row">
            <label className="form-label">Priority</label>
            <select className="form-input" value={form.priority} onChange={e=>upd('priority',e.target.value)}>
              {Object.entries(PRIORITY_META).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div className="form-row">
            <label className="form-label">Project Color</label>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:4 }}>
              {PROJECT_COLORS.map(c=>(
                <div key={c} onClick={()=>upd('color',c)} style={{
                  width:24, height:24, borderRadius:'50%', background:c, cursor:'pointer',
                  border: form.color===c ? '3px solid white' : '2px solid transparent',
                  boxShadow: form.color===c ? `0 0 0 2px ${c}` : 'none',
                  transition:'all .15s',
                }} />
              ))}
            </div>
          </div>
          <div className="form-row">
            <label className="form-label">Start Date</label>
            <input type="date" className="form-input" value={form.start_date} onChange={e=>upd('start_date',e.target.value)} />
          </div>
          <div className="form-row">
            <label className="form-label">Due Date</label>
            <input type="date" className="form-input" value={form.due_date} onChange={e=>upd('due_date',e.target.value)} />
          </div>
        </div>

        <div className="form-row" style={{ marginTop:14 }}>
          <label className="form-label">Team Members</label>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:6 }}>
            {users?.filter(u=>u.role!=='accountant').map(u=>(
              <div key={u.id} onClick={()=>toggleMember(u.id)} style={{
                padding:'5px 12px', borderRadius:20, fontSize:12, cursor:'pointer', userSelect:'none',
                background: form.member_ids.includes(u.id) ? 'var(--accent)' : 'var(--surface2)',
                color: form.member_ids.includes(u.id) ? '#fff' : 'var(--text2)',
                fontWeight: form.member_ids.includes(u.id) ? 700 : 400,
                transition:'all .15s',
              }}>
                {u.full_name}
              </div>
            ))}
          </div>
        </div>

        <div className="modal-actions" style={{ marginTop:20 }}>
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Update Project' : 'Create Project'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Project Card ──────────────────────────────────────────────────────────────
function ProjectCard({ project, viewedMonth, onEdit, onOpen, onDelete, canManage }) {
  const pct    = progress(project);
  const days   = daysDue(project.due_date);
  const status = STATUS_META[project.status] || STATUS_META.active;
  const overdue = parseInt(project.overdue_tasks)||0;
  const isCancelled = project.status === 'cancelled';
  // Still open, and it didn't originate this month — it's only showing up because open
  // projects always carry forward into the current month view.
  const isCarriedForward = !project.closed_at && project.created_month !== viewedMonth;

  // Progress bar colour: green only when completed status; grey for cancelled
  const barColor = isCancelled ? '#9CA3AF'
    : project.status === 'completed' ? '#059669'
    : project.color;
  const pctColor = isCancelled ? 'var(--text3)'
    : pct === 100 ? '#059669'
    : 'var(--text2)';

  return (
    <div className="card" style={{
      padding:0, overflow:'hidden', cursor:'pointer',
      transition:'transform .15s, box-shadow .15s',
      border:`1px solid var(--border)`,
      opacity: isCancelled ? 0.75 : 1,
    }}
      onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.12)';}}
      onMouseLeave={e=>{e.currentTarget.style.transform='';e.currentTarget.style.boxShadow='';}}
      onClick={() => onOpen(project)}
    >
      {/* Color bar */}
      <div style={{ height:4, background: isCancelled ? '#9CA3AF' : project.color }} />

      <div style={{ padding:'16px 18px' }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:10 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:700, fontSize:15, color:'var(--text1)', marginBottom:3,
              textDecoration: isCancelled ? 'line-through' : 'none' }}>{project.name}</div>
            {project.client_name && <div style={{ fontSize:11, color:'var(--text3)' }}>{project.client_name}</div>}
            {isCarriedForward && (
              <div title={`Still open since ${monthLabel(project.created_month)} — carried forward to this month`}
                style={{ display:'inline-flex', alignItems:'center', gap:3, marginTop:4, fontSize:10, fontWeight:600, color:'#B45309', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:10, padding:'1px 7px' }}>
                ↩ Carried forward from {monthLabel(project.created_month)}
              </div>
            )}
          </div>
          <div style={{ display:'flex', gap:6, alignItems:'center' }}>
            <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:10, background:status.bg, color:status.color }}>
              {status.label}
            </span>
            {canManage && <>
              <button className="btn-ghost" style={{ padding:'2px 6px', fontSize:11 }}
                onClick={e=>{e.stopPropagation();onEdit(project);}}>✎</button>
              <button className="btn-ghost" style={{ padding:'2px 6px', fontSize:11, color:'#DC2626' }}
                onClick={e=>{e.stopPropagation();onDelete(project);}}>🗑</button>
            </>}
          </div>
        </div>

        {project.description && (
          <div style={{ fontSize:12, color:'var(--text2)', marginBottom:10, lineHeight:1.4,
            display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
            {project.description}
          </div>
        )}

        {/* Progress bar */}
        <div style={{ marginBottom:10 }}>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--text3)', marginBottom:4 }}>
            <span>{project.done_tasks}/{project.total_tasks} tasks</span>
            <span style={{ fontWeight:600, color: pctColor }}>{pct}%</span>
          </div>
          <div style={{ height:6, background:'var(--surface2)', borderRadius:3, overflow:'hidden' }}>
            <div style={{ height:'100%', width:`${pct}%`, borderRadius:3, transition:'width .4s', background: barColor }} />
          </div>
        </div>

        {/* Footer */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          {/* Avatars */}
          <div style={{ display:'flex' }}>
            {(project.members||[]).slice(0,4).map((m,i)=>(
              <div key={m.user_id} title={m.full_name} style={{
                width:24, height:24, borderRadius:'50%', background: isCancelled ? '#9CA3AF' : project.color,
                color:'#fff', fontSize:10, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center',
                marginLeft: i>0 ? -6 : 0, border:'2px solid var(--surface)', zIndex:4-i,
              }}>
                {m.full_name[0].toUpperCase()}
              </div>
            ))}
            {(project.members||[]).length > 4 && (
              <div style={{ width:24,height:24,borderRadius:'50%',background:'var(--surface2)',
                color:'var(--text3)',fontSize:10,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',
                marginLeft:-6,border:'2px solid var(--surface)' }}>
                +{project.members.length-4}
              </div>
            )}
          </div>
          {/* Due date */}
          <div style={{ fontSize:11, color: days===null?'var(--text3)':days<0?'#DC2626':days<=3?'#D97706':'var(--text3)', fontWeight: days!==null&&days<=3?700:400 }}>
            {overdue>0 && !isCancelled && <span style={{ color:'#DC2626', marginRight:8 }}>⚠ {overdue} overdue</span>}
            {days===null ? '—' : days<0 ? `${Math.abs(days)}d overdue` : days===0 ? 'Due today' : `Due in ${days}d`}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ProjectsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { isOwner, user } = useAuth();

  const [showModal, setShowModal] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [month, setMonth] = useState(getInitialMonth);
  useEffect(() => { const h = e => setMonth(e.detail); window.addEventListener('monthChange', h); return () => window.removeEventListener('monthChange', h); }, []);

  const { data: projects=[], isLoading } = useQuery({
    queryKey: ['projects', month],
    queryFn: () => api.get(`/projects?month=${month}`).then(r=>r.data),
  });
  const { data: clients=[] } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.get('/clients').then(r=>r.data),
  });
  const { data: users=[] } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get('/users').then(r=>r.data),
  });

  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/projects/${id}`),
    onSuccess: () => { qc.invalidateQueries(['projects']); setDeleteConfirm(null); toast.success('Project deleted'); },
    onError: () => toast.error('Failed to delete'),
  });

  const filtered = projects
    .filter(p => filterStatus==='all' || p.status===filterStatus)
    .filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.client_name||'').toLowerCase().includes(search.toLowerCase()));

  const stats = {
    total:     projects.length,
    active:    projects.filter(p=>p.status==='active').length,
    overdue:   projects.reduce((s,p)=>s+parseInt(p.overdue_tasks||0),0),
    completed: projects.filter(p=>p.status==='completed').length,
  };

  return (
    <div>
      <div style={{ fontSize:12, color:'var(--text2)', marginBottom:14 }}>
        {month === new Date().toISOString().slice(0,7)
          ? <>Showing every still-open project (from any month) plus whatever was completed or cancelled in <strong>{monthLabel(month)}</strong>.</>
          : <>Showing only what was completed or cancelled in <strong>{monthLabel(month)}</strong> — still-open projects always appear under the current month instead.</>}
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:24 }}>
        {[
          ['Total Projects', stats.total, '#073E81'],
          ['Active',         stats.active, '#059669'],
          ['Overdue Tasks',  stats.overdue, '#DC2626'],
          ['Completed',      stats.completed, '#7C3AED'],
        ].map(([label,val,color])=>(
          <div key={label} className="card" style={{ padding:'16px 20px', display:'flex', alignItems:'center', gap:14 }}>
            <div style={{ fontSize:28, fontWeight:800, color }}>{val}</div>
            <div style={{ fontSize:12, color:'var(--text2)' }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <input className="form-input" placeholder="Search projects…" value={search}
          onChange={e=>setSearch(e.target.value)} style={{ width:220 }} />
        <div style={{ display:'flex', gap:4 }}>
          {['all',...Object.keys(STATUS_META)].map(s=>(
            <button key={s} onClick={()=>setFilterStatus(s)}
              style={{ padding:'5px 12px', borderRadius:6, fontSize:12, border:'1px solid var(--border)',
                fontWeight: filterStatus===s?700:400,
                background: filterStatus===s?'var(--accent)':'var(--surface)',
                color: filterStatus===s?'#fff':'var(--text2)', cursor:'pointer' }}>
              {s==='all' ? 'All' : STATUS_META[s].label}
            </button>
          ))}
        </div>
        <div style={{ flex:1 }} />
        {(isOwner || user?.role==='admin') && (
          <button className="btn-primary" onClick={()=>{ setEditProject(null); setShowModal(true); }}>
            + New Project
          </button>
        )}
      </div>

      {/* Grid */}
      {isLoading && <div className="loading-screen"><div className="spinner"/></div>}
      {!isLoading && filtered.length===0 && (
        <div className="card" style={{ padding:60, textAlign:'center' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>📋</div>
          <div style={{ fontWeight:700, fontSize:16 }}>No projects yet</div>
          <div style={{ color:'var(--text2)', marginTop:6, marginBottom:20 }}>Create your first project to get started</div>
          {(isOwner||user?.role==='admin') && <button className="btn-primary" onClick={()=>setShowModal(true)}>+ New Project</button>}
        </div>
      )}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:16 }}>
        {filtered.map(p=>(
          <ProjectCard key={p.id} project={p} viewedMonth={month}
            onOpen={p=>navigate(`/projects/${p.id}`)}
            onEdit={p=>{ setEditProject(p); setShowModal(true); }}
            onDelete={p=>setDeleteConfirm(p)}
            canManage={isOwner || user?.role==='admin'}
          />
        ))}
      </div>

      {showModal && (
        <ProjectModal
          project={editProject}
          clients={clients}
          users={users}
          onClose={()=>{ setShowModal(false); setEditProject(null); }}
          onSaved={()=>qc.invalidateQueries(['projects'])}
        />
      )}

      {deleteConfirm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'var(--surface)', borderRadius:12, padding:28, maxWidth:420, width:'90%', boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }}>
            <div style={{ fontWeight:700, fontSize:16, marginBottom:8, color:'#DC2626' }}>Delete Project?</div>
            <div style={{ color:'var(--text-muted)', fontSize:14, marginBottom:6 }}>
              This will permanently delete <strong>{deleteConfirm.name}</strong> and all its tasks, comments, and activity history.
            </div>
            <div style={{ fontSize:13, color:'#DC2626', marginBottom:20, fontWeight:600 }}>This cannot be undone.</div>
            <div style={{ display:'flex', gap:10 }}>
              <button className="btn-ghost" style={{ flex:1 }} onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button style={{ flex:1, padding:'8px 16px', borderRadius:8, background:'#DC2626', color:'#fff', border:'none', fontWeight:700, cursor:'pointer' }}
                onClick={() => deleteMutation.mutate(deleteConfirm.id)}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
